<template lang="pug">
  extends ../../Tabs/tabsView.pug
  block more-actions
    LfLabelButtonComponent(
        v-if="showIAButton && !this.isNewRegister"
        class="ia-search-button"
        :style="{ minWidth: '140px' }"
        :text="$t('views.search_ia.title')"
        :action="goToIASearch"
        :prependIconSrc="iaIcon"
        :variant="LabelButtonVariant.OUTLINED"
        size="md"
      )
    div(aria-hidden="true" v-if="showIAButton && !this.isNewRegister" class="ia-search-divider")
  block tabHeader
</template>
<script lang="ts">
import Vue from 'vue'
import { Action, Getter } from 'vuex-class'
import { Component } from 'vue-property-decorator'
import { ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import { ContactLegalPersonTypeId, ContactPersonTypeId } from '@/store/modules/contacts/contactsTypes'
import {
  ContactFields,
  NaturalFields,
  LegalFields,
  DEFAULT_LEGAL_COUNTRY,
  DEFAULT_EAS_CODE
} from './types/ContactFieldTypes'
import TabsView from '@/views/Tabs/TabsView.vue'
import LfLabelButtonComponent from '@/components/forms/buttons/label/LfLabelButtonComponent.vue'
import { LabelButtonVariant as LabelButtonVariantEnum } from '@/components/forms/buttons/types/ButtonTypes'
import { useGoToIASearch } from '@/modules/documentSearcher/composables/useGoToIASearch'
import { TrackerEvents, trackEvent } from '@/plugins/tracker'

const formsModule: string = ModuleNamespaces.FORMS
const entitiesModule: string = ModuleNamespaces.ENTITIES
const authModule = ModuleNamespaces.AUTH

@Component({
  components: {
    LfLabelButtonComponent
  }
})
export default class ContactDetailView extends TabsView {
  @Action('saveSelectedEntityName', { namespace: entitiesModule })
  saveSelectedEntityName: (type: string) => void

  @Action('prepareRegisterFormDataToSave', { namespace: formsModule })
  prepareFormData: ({}) => void

  @Getter('getRegisterFormData', { namespace: formsModule })
  getFormData: () => object

  @Getter('getShowSearchIA', { namespace: authModule })
  showIAButton: boolean

  iaIcon = require('@/assets/images/svg/icon-ia.svg')

  LabelButtonVariant = LabelButtonVariantEnum

  contactPersonTypeName = `${this.$t('routes.new_f')} ${this.$t('navigation.drawer-menu-items.person')}`

  texts = {
    nameNatural: this.$t('components.contact.name_natural'),
    nameLegal: this.$t('components.contact.name_legal')
  }
  created() {
    this.saveSelectedEntityName(this.getContext)
  }

  async onInitForm(params: any) {
    const { schema, formData, isNewForm } = params
    this.isNewRegister = isNewForm
    const selectedRegisterData = this.getSelectedRegisterData
    if (selectedRegisterData?.isoLegalCountry) {
      formData.legalCountry = {
        id: String(selectedRegisterData.idLegalCountry),
        iso: selectedRegisterData.isoLegalCountry
      }
    } else {
      formData.legalCountry = DEFAULT_LEGAL_COUNTRY
      formData.easCode = DEFAULT_EAS_CODE
    }

    this.formLoaded = true
    await this.disableFieldInFieldset(ContactFields.PROFESSIONAL_TYPE, ContactFields.CHARTERED_FIELDS, schema, true)
  }

  get breadcrumbsText() {
    if (this.getSelectedRegisterData) {
      return this.getSelectedRegisterData.name
        ? this.getSelectedRegisterData.name + ' ' + (this.getSelectedRegisterData.firstSurname || '')
        : this.contactPersonTypeName.charAt(0).toUpperCase() + this.contactPersonTypeName.slice(1).toLowerCase()
    }
    return ''
  }

  onFieldChanged(schema: any, formData: any, field: any, value: any) {
    if (typeof formData[ContactFields.EAS_CODE] === 'string' && formData[ContactFields.EAS_CODE]) {
      formData[ContactFields.EAS_CODE] = { code: formData[ContactFields.EAS_CODE] }
    }
    if (ContactFields.PERSON_TYPE === field.id) {
      const isNatural = value === ContactPersonTypeId.NATURAL
      const nameItem: any = schema.find((item: any) => item.id === ContactFields.NAME)
      if (nameItem) {
        nameItem.label = isNatural ? this.texts.nameNatural : this.texts.nameLegal
      }
      LegalFields.forEach((item) => this.changehidden(item, schema, isNatural))
      NaturalFields.forEach((item) => this.changehidden(item, schema, !isNatural))
      this.changehiddenFieldSet(ContactFields.CHARTERED_FIELDS, schema, !isNatural)
      this.changehiddenFieldSet(
        ContactFields.CHARTERED_EXTRA_FIELDS,
        schema,
        isNatural ? !formData[ContactFields.PROFESSIONAL_TYPE] : true
      )
    }
    if (ContactFields.LEGAL_TYPE === field.id) {
      this.changehiddenFieldSet(ContactFields.AAPP_FIELDS, schema, value !== ContactLegalPersonTypeId.AAPP)
    }
    if (ContactFields.CHARTERED === field.id) {
      const disabled = '0' === value
      this.disableFieldInFieldset(ContactFields.PROFESSIONAL_TYPE, ContactFields.CHARTERED_FIELDS, schema, disabled)
      this.changehiddenFieldSet(
        ContactFields.CHARTERED_EXTRA_FIELDS,
        schema,
        formData[ContactFields.PROFESSIONAL_TYPE] === undefined || disabled
      )
    }
    if (ContactFields.PROFESSIONAL_TYPE === field.id) {
      const hidden = null === value
      this.changehiddenFieldSet(ContactFields.CHARTERED_EXTRA_FIELDS, schema, hidden)
    }
    if (ContactFields.LEGAL_COUNTRY === field.id) {
      if (!value) {
        this.$nextTick(() => {
          Vue.set(formData, 'legalCountry', DEFAULT_LEGAL_COUNTRY)
          Vue.set(formData, 'easCode', DEFAULT_EAS_CODE)
          this.$store.commit('formsModule/SET_RELATED_DATA', {
            data: DEFAULT_LEGAL_COUNTRY.iso,
            relatedAlias: ContactFields.LEGAL_COUNTRY
          })
        })
      }
    }
    if (ContactFields.UNNUMBERED === field.id) {
      const disabled = '1' === value
      const locationFields = schema.find((item: any) => {
        return item.fieldsetId === ContactFields.LOCATION_FIELDS
      })
      this.disableField(ContactFields.NUMBER, locationFields.fields, disabled)
    }
  }

  changehidden(fieldId: string, schema: any, hidden: boolean) {
    const field = schema.find((item: any) => {
      return item.id === fieldId
    })
    field.hidden = hidden
  }

  disableField(fieldId: string, schema: any, disabled: boolean) {
    const field = schema.find((item: any) => {
      return item.id === fieldId
    })
    field.disabled = disabled
  }

  disableFieldInFieldset(fieldId: string, fieldSet: string, schema: any, disabled: boolean) {
    const fieldsInFieldset = schema.find((item: any) => {
      return item.fieldsetId === fieldSet
    })
    fieldsInFieldset.fields.map((item: any) => {
      if (item.id === fieldId) {
        item.disabled = disabled
      }
      return item
    })
  }

  changehiddenFieldSet(fieldSet: string, schema: any, hidden: boolean) {
    const fieldsInFieldset = schema.find((item: any) => {
      if (item.fieldsetId === fieldSet) {
        item.hidden = hidden
      }
      return item.fieldsetId === fieldSet
    })
    fieldsInFieldset.fields.map((item: any) => (item.hidden = hidden))
  }

  goToIASearch() {
    const { goToIASearchWithContact } = useGoToIASearch()
    trackEvent(TrackerEvents.CLICK_AI_SEARCH, { click_origin: 'contact' })
    goToIASearchWithContact(this.getSelectedRegisterData, this.entityId)
  }

  async save(redirectToGridTable = true, resolve: any = null, _idObjectType = '') {
    const formData: any = this.getFormData
    this.prepareFormData(formData)

    const { type } = this.entity(this.getContext)
    const { canSave } = this.checkEntityPermissionsGetter(type)
    if (canSave) {
      await this.saveRegisterFormData({
        endpoint: this.endpoints(this.entity(this.getContext).type).save,
        idSelectedRegister: this.entity(this.getContext).id,
        goToURL: redirectToGridTable ? this.reviewRedirect() : null,
        context: this.getContext
      })
    }

    if (resolve) {
      resolve()
    }
  }
}
</script>

<style lang="scss">
.ia-search-divider {
  align-self: stretch;
  border-left: 1px solid $neutral-grey-300;
  margin: $spacing-xxs $spacing-xs $spacing-nule $spacing-md;
  width: 1px;
}
</style>
